import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Coffee, Wifi, WifiOff, RefreshCw, Power, PowerOff, ChevronRight, AlertTriangle, Info } from "lucide-react";
import ConnectorCard from "@/components/ConnectorCard";

// Phase 36.24 — Square POS panel
// Phase 25.7  — Switched to ConnectorCard pattern (mobile-friendly).

const POS_INDUSTRIES = new Set([
  "retail", "hospitality", "consumer goods", "consumer_goods",
  "food & beverage", "f&b", "ecommerce", "e-commerce", "fmcg", "restaurants",
]);

function fmtTime(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  const diffMs = Date.now() - dt.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)} h ago`;
  return dt.toLocaleString();
}

export default function SquareConnectionPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [industry, setIndustry] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, w] = await Promise.all([
        api.get("/pos/square/status"),
        api.get("/workspace").catch(() => ({ data: {} })),
      ]);
      setStatus(r.data);
      setIndustry((w.data?.industry || "").toLowerCase().trim());
    } catch {
      setStatus({ connected: false, _err: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/pos/square/connect");
      if (r.data.authorize_url) {
        window.location.href = r.data.authorize_url;
      } else {
        toast.error("No authorization URL returned");
        setConnecting(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Connect failed";
      toast.error(detail);
      if (detail.includes("not configured")) {
        toast.info("Paste your Square keys under Settings → Platform Keys first.", { duration: 8000 });
      }
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Square POS? Existing transactions will be kept; new ones will stop syncing.")) return;
    setBusy(true);
    try {
      await api.post("/pos/square/disconnect");
      toast.success("Square POS disconnected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  // Phase 25.13w — Hard-delete the stored Square connection entirely.
  // Used when the row is already disconnected/corrupt (e.g. stale sandbox
  // tokens from a previous environment) and the operator wants a fully
  // clean slate before re-connecting.
  const wipe = async () => {
    if (!confirm("WIPE this Square connection entirely? The card will disappear and you'll need to re-connect from scratch. Historical transactions are preserved.")) return;
    setBusy(true);
    try {
      await api.post("/pos/square/wipe");
      toast.success("Square connection wiped clean");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally {
      setBusy(false);
    }
  };

  const resync = async () => {
    setBusy(true);
    try {
      const r = await api.post("/pos/square/backfill");
      if (r.data.ok) {
        toast.success(`Re-synced — ${r.data.inserted} transactions inserted`);
      } else {
        toast.error(`Sync failed: ${r.data.reason}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (status?._err) return null;

  const isPosWorkspace = industry && POS_INDUSTRIES.has(industry);
  const hasAnyConnection = status?.connected;
  if (!isPosWorkspace && !hasAnyConnection) return null;

  const connected = status?.connected;
  const isActive = connected && status.status === "active";
  const isSandbox = status?.environment === "sandbox";

  const cardStatus = !connected
    ? "none"
    : isActive
      ? "connected"
      : "disconnected";

  const statusLabel = !connected
    ? "NOT CONNECTED"
    : isActive
      ? "● LIVE"
      : "DISCONNECTED";

  const badges = (
    <>
      {isSandbox && (
        <span className="px-2 py-0.5 border border-[#FFB000]/40 bg-[#FFB000]/5 text-[#FFB000] font-mono text-[9px] uppercase tracking-widest">
          SANDBOX
        </span>
      )}
      {status?.currency && (
        <span className="px-2 py-0.5 border border-zinc-800 bg-zinc-950 text-zinc-400 font-mono text-[9px] uppercase tracking-widest">
          {status.currency}
        </span>
      )}
    </>
  );

  return (
    <ConnectorCard
      title="// SQUARE POS · LIVE TRANSACTION FEED"
      testId="square-connection-panel"
      icon={connected ? (isActive ? Wifi : WifiOff) : Coffee}
      name={connected ? (status.business_name || status.location_name || "Square Merchant") : "Connect Square POS"}
      tagline={
        connected
          ? [
              status.location_name && `📍 ${status.location_name}`,
              `Connected ${fmtTime(status.connected_at)}`,
              status.last_sync_at && `Last sync ${fmtTime(status.last_sync_at)}`,
            ].filter(Boolean).join(" · ")
          : "Real-time transaction feed — every payment, tip, refund streams into POS Live. 30-day backfill on first connect. Sandbox + Production."
      }
      status={cardStatus}
      statusLabel={statusLabel}
      badges={badges}
      primaryAction={
        !connected ? (
          <button
            onClick={connect}
            disabled={connecting}
            data-testid="square-connect-btn"
            className="w-full sm:w-auto bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <Power className="w-3.5 h-3.5" />
            {connecting ? "OPENING…" : "CONNECT SQUARE"}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        ) : isActive ? (
          <button
            onClick={resync}
            disabled={busy}
            data-testid="square-resync-btn"
            className="w-full sm:w-auto bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
            RE-SYNC 30D
          </button>
        ) : (
          <button
            onClick={connect}
            disabled={connecting}
            data-testid="square-reconnect-btn"
            className="w-full sm:w-auto bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <Power className="w-3.5 h-3.5" />
            {connecting ? "OPENING…" : "RECONNECT"}
          </button>
        )
      }
      secondaryActions={connected ? (
        <button
          onClick={isActive ? disconnect : wipe}
          disabled={busy}
          data-testid={isActive ? "square-disconnect-btn" : "square-wipe-btn"}
          className="w-full sm:w-auto border border-[#FF6B47]/40 text-[#FF6B47] hover:bg-[#FF6B47]/10 px-3 py-2.5 font-mono text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <PowerOff className="w-3.5 h-3.5" />
          {isActive ? "DISCONNECT" : "WIPE"}
        </button>
      ) : null}
      extra={
        <>
          {connected && !isActive && (
            <div className="flex items-start gap-2 border border-[#FF6B47]/30 bg-[#FF6B47]/5 p-3">
              <AlertTriangle className="w-4 h-4 text-[#FF6B47] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-zinc-300">
                Connection lost. Tap <strong className="text-white">RECONNECT</strong> to re-link this workspace.
              </div>
            </div>
          )}
          {isActive && isSandbox && (
            <div data-testid="square-sandbox-hint" className="flex items-start gap-2 border border-[#FFB000]/30 bg-[#FFB000]/5 p-3">
              <Info className="w-4 h-4 text-[#FFB000] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-zinc-300 leading-relaxed">
                You're in <strong className="text-[#FFB000]">SANDBOX</strong>. To onboard real merchants, switch{" "}
                <code className="font-mono text-[10px] bg-black px-1.5 py-0.5 border border-zinc-800">SQUARE_ENV</code> to{" "}
                <code className="font-mono text-[10px] bg-black px-1.5 py-0.5 border border-zinc-800">production</code> in{" "}
                <strong className="text-white">Settings → Platform Keys</strong>.
              </div>
            </div>
          )}
        </>
      }
    />
  );
}
